import { ForwardedRef } from 'react';

import { fabric } from 'fabric';

import {
  BACKGROUND_OVERLAY,
  CANVAS_GUIDELINE_COLOR,
  CANVAS_GUIDELINE_LINE_WIDTH,
  CANVAS_SCALE_MARGIN,
  CANVAS_SNAP_THRESHOLD,
  CANVAS_THEME_CONFIG,
  DEFAULT_CANVAS_CONTENT_JSON,
  DEFAULT_CANVAS_SIZE,
  DEFAULT_CANVAS_VIEWPORT_TRANSFORM,
  FULL_HD_DIMENSION
} from '@/constants';
import { isVideoAsset } from '@/helpers/AssetHelper';
import {
  ActiveGuides,
  CanvasContentJson,
  CanvasField,
  CanvasTheme,
  GuideAxis,
  ObjectMetrics,
  PrepareCanvasContentOptions,
  ViewportMatrix
} from '@/types';

const getCanvasDimensionScales = (id: string, width: number, height: number) => {
  const canvasParentElement = document.getElementById(id);
  if (!canvasParentElement) {
    return null;
  }
  const availableWidth = canvasParentElement.clientWidth;
  const availableHeight = canvasParentElement.clientHeight;

  const widthScale = availableWidth / width;
  const heightScale = availableHeight / height;
  return { widthScale, heightScale };
};

export const getCanvasScale = (id: string, width: number, height: number) => {
  const scales = getCanvasDimensionScales(id, width, height);
  if (!scales) {
    return 1;
  }
  return Math.min(scales.widthScale, scales.heightScale);
};

export const addDefaultFields = (
  canvas: fabric.Canvas,
  fields: CanvasField[],
  theme: CanvasTheme,
  moveObjects: boolean
) => {
  fields?.forEach((field, index) => {
    const themeToApply = index % 2 === 0 ? theme.textSecondary : theme.text;
    if (!field) {
      return;
    }
    //Refactor this.
    const fieldEl = new fabric.Textbox(field.value, {
      width: themeToApply.width,
      left: themeToApply.left,
      top: themeToApply.top + 120 * index,
      lineHeight: themeToApply.lineHeight,
      isWrapping: true,
      originX: 'center',
      originY: 'center',
      splitByGrapheme: false,
      fill: themeToApply.textColor,
      textAlign: themeToApply.textAlign,
      fontSize: themeToApply.fontSize,
      fontWeight: themeToApply.fontWeight,
      fontFamily: themeToApply.fontFamily,
      padding: 20,
      lockMovementX: !moveObjects,
      lockMovementY: !moveObjects,
      lockScalingX: !moveObjects,
      lockScalingY: !moveObjects,
      lockRotation: !moveObjects,
      editable: false,
      selectable: moveObjects,
      hoverCursor: 'default',
      hasControls: false,
      hasBorders: false,
      data: {
        id: field.label
      }
    });

    fieldEl.setControlsVisibility({
      mt: false,
      mb: false,
      ml: false,
      mr: false
    });

    canvas.add(fieldEl);
  });
  canvas.requestRenderAll();
};

export const getClipScale = (width: number, height: number) => {
  const scaleX = CANVAS_THEME_CONFIG.clipImage.maxWidth / width;
  const scaleY = CANVAS_THEME_CONFIG.clipImage.maxHeight / height;
  return scaleY < scaleX ? scaleY : scaleX;
};

export const getOverlayFromObjects = (objects: fabric.Object[]) => {
  return objects
    .filter((o) => o.type === 'rect')
    .find((o) => (o as fabric.Rect).data?.id === BACKGROUND_OVERLAY);
};
// Check if we need this
export const getBaseCanvasSizeFromContent = (content: {
  width?: number;
  height?: number;
  viewportTransform?: number[];
}) => {
  const viewportTransform =
    Array.isArray(content.viewportTransform) && content.viewportTransform.length === 6
      ? content.viewportTransform
      : DEFAULT_CANVAS_VIEWPORT_TRANSFORM;

  return {
    width: Math.floor((content.width ?? 0) / viewportTransform[0]),
    height: Math.floor((content.height ?? 0) / viewportTransform[3])
  };
};

// Canvas content
export const getCanvasContent = (
  canvasJson: string | null,
  { backgroundAsset, profilePhoto }: PrepareCanvasContentOptions
): CanvasContentJson => {
  const content = JSON.parse(
    canvasJson ?? JSON.stringify(DEFAULT_CANVAS_CONTENT_JSON)
  ) as CanvasContentJson;

  if ((!backgroundAsset || isVideoAsset(backgroundAsset)) && content.backgroundImage) {
    delete content.backgroundImage;
  }

  if (!profilePhoto && Array.isArray(content.objects) && content.objects.length > 0) {
    content.objects = content.objects.filter((obj) => obj.data?.id !== 'profileImage');
  }

  return content;
};

export const resizeCanvasFromContent = (
  canvas: fabric.Canvas,
  contentJson: CanvasContentJson,
  containerId: string
) => {
  const { width = 0, height = 0 } = contentJson;

  const viewportTransform =
    Array.isArray(contentJson.viewportTransform) && contentJson.viewportTransform.length === 6
      ? contentJson.viewportTransform
      : DEFAULT_CANVAS_VIEWPORT_TRANSFORM;

  const canvasWidth = width / viewportTransform[0];
  const canvasHeight = height / viewportTransform[3];

  const scale = Math.round(getCanvasScale(containerId, canvasWidth, canvasHeight) * 100) / 100;

  resizeCanvas(canvas, scale, Math.floor(canvasWidth), Math.floor(canvasHeight));
};

export const lockCanvasObjects = (canvas: fabric.Canvas) => {
  canvas.getObjects().forEach((obj) => {
    obj.set({
      selectable: false,
      evented: false,
      hasControls: false,
      lockMovementX: true,
      lockMovementY: true,
      hasBorders: false
    });
    obj.dirty = true;
  });
};

// Snapping and Guidelines
const getObjectMetrics = (obj: fabric.Object): ObjectMetrics => {
  const center = obj.getCenterPoint();
  const width = obj.getScaledWidth();
  const height = obj.getScaledHeight();

  return {
    width,
    height,
    centerX: center.x,
    centerY: center.y,
    left: center.x - width / 2,
    right: center.x + width / 2,
    top: center.y - height / 2,
    bottom: center.y + height / 2
  };
};

const getCanvasSize = (canvas: fabric.Canvas) => {
  const zoom = canvas.getZoom() || 1;

  return {
    width: canvas.getWidth() / zoom,
    height: canvas.getHeight() / zoom
  };
};

const getCanvasCenter = (canvas: fabric.Canvas) => {
  const { width, height } = getCanvasSize(canvas);

  return {
    x: width / 2,
    y: height / 2
  };
};

const getTopRenderContext = (canvas: fabric.Canvas) =>
  (canvas as fabric.Canvas & { contextTop?: CanvasRenderingContext2D }).contextTop;

const getViewportMatrix = (canvas: fabric.Canvas): ViewportMatrix =>
  (canvas.viewportTransform as ViewportMatrix | undefined) ?? DEFAULT_CANVAS_VIEWPORT_TRANSFORM;

const toViewportPoint = (canvas: fabric.Canvas, x: number, y: number) =>
  fabric.util.transformPoint(new fabric.Point(x, y), getViewportMatrix(canvas));

const clearGuideLayer = (canvas: fabric.Canvas) => {
  const ctx = getTopRenderContext(canvas);

  if (!ctx) {
    return;
  }

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.restore();
};

const applySnapX = (
  movingObject: fabric.Object,
  movingMetrics: ObjectMetrics,
  nextCenterX: number
) => {
  movingObject.setPositionByOrigin(
    new fabric.Point(nextCenterX, movingMetrics.centerY),
    'center',
    'center'
  );

  movingMetrics.centerX = nextCenterX;
  movingMetrics.left = nextCenterX - movingMetrics.width / 2;
  movingMetrics.right = nextCenterX + movingMetrics.width / 2;
};

const applySnapY = (
  movingObject: fabric.Object,
  movingMetrics: ObjectMetrics,
  nextCenterY: number
) => {
  movingObject.setPositionByOrigin(
    new fabric.Point(movingMetrics.centerX, nextCenterY),
    'center',
    'center'
  );

  movingMetrics.centerY = nextCenterY;
  movingMetrics.top = nextCenterY - movingMetrics.height / 2;
  movingMetrics.bottom = nextCenterY + movingMetrics.height / 2;
};

const updateClosestGuide = (
  guides: ActiveGuides,
  axis: GuideAxis,
  currentMinDiff: number,
  diff: number,
  guideValue: number
) => {
  if (diff < currentMinDiff) {
    guides[axis] = guideValue;
    return diff;
  }

  return currentMinDiff;
};

export const initCanvasSnapping = (
  canvas: fabric.Canvas,
  snapThreshold = CANVAS_SNAP_THRESHOLD
) => {
  const handleObjectMoving = (e: fabric.IEvent) => {
    const movingObject = e.target;

    if (!movingObject) {
      return;
    }

    const movingMetrics = getObjectMetrics(movingObject);

    canvas.getObjects().forEach((obj) => {
      if (obj === movingObject || !obj.visible) {
        return;
      }

      const objMetrics = getObjectMetrics(obj);

      // Snap left edge to left edge
      if (Math.abs(movingMetrics.left - objMetrics.left) < snapThreshold) {
        applySnapX(movingObject, movingMetrics, objMetrics.left + movingMetrics.width / 2);
      }

      // Snap right edge to right edge
      if (Math.abs(movingMetrics.right - objMetrics.right) < snapThreshold) {
        applySnapX(movingObject, movingMetrics, objMetrics.right - movingMetrics.width / 2);
      }

      // Snap top edge to top edge
      if (Math.abs(movingMetrics.top - objMetrics.top) < snapThreshold) {
        applySnapY(movingObject, movingMetrics, objMetrics.top + movingMetrics.height / 2);
      }

      // Snap bottom edge to bottom edge
      if (Math.abs(movingMetrics.bottom - objMetrics.bottom) < snapThreshold) {
        applySnapY(movingObject, movingMetrics, objMetrics.bottom - movingMetrics.height / 2);
      }

      // Snap center X to center X
      if (Math.abs(movingMetrics.centerX - objMetrics.centerX) < snapThreshold) {
        applySnapX(movingObject, movingMetrics, objMetrics.centerX);
      }

      // Snap center Y to center Y
      if (Math.abs(movingMetrics.centerY - objMetrics.centerY) < snapThreshold) {
        applySnapY(movingObject, movingMetrics, objMetrics.centerY);
      }
    });

    const canvasCenter = getCanvasCenter(canvas);

    if (Math.abs(movingMetrics.centerX - canvasCenter.x) < snapThreshold) {
      applySnapX(movingObject, movingMetrics, canvasCenter.x);
    }

    if (Math.abs(movingMetrics.centerY - canvasCenter.y) < snapThreshold) {
      applySnapY(movingObject, movingMetrics, canvasCenter.y);
    }

    movingObject.setCoords();
  };

  canvas.on('object:moving', handleObjectMoving);

  return () => {
    canvas.off('object:moving', handleObjectMoving);
  };
};

export const initCanvasGuidelines = (
  canvas: fabric.Canvas,
  snapThreshold = CANVAS_SNAP_THRESHOLD
) => {
  const guides: ActiveGuides = {
    vertical: null,
    horizontal: null
  };

  const resetGuides = () => {
    guides.vertical = null;
    guides.horizontal = null;
  };

  const handleMoving = (e: fabric.IEvent) => {
    const movingObject = e.target;

    if (!movingObject) {
      return;
    }

    resetGuides();

    const movingMetrics = getObjectMetrics(movingObject);

    let minDiffX = snapThreshold;
    let minDiffY = snapThreshold;

    canvas.getObjects().forEach((obj) => {
      if (obj === movingObject || !obj.visible) {
        return;
      }

      const objMetrics = getObjectMetrics(obj);

      minDiffX = updateClosestGuide(
        guides,
        'vertical',
        minDiffX,
        Math.abs(movingMetrics.left - objMetrics.left),
        objMetrics.left
      );

      minDiffX = updateClosestGuide(
        guides,
        'vertical',
        minDiffX,
        Math.abs(movingMetrics.right - objMetrics.right),
        objMetrics.right
      );

      minDiffX = updateClosestGuide(
        guides,
        'vertical',
        minDiffX,
        Math.abs(movingMetrics.centerX - objMetrics.centerX),
        objMetrics.centerX
      );

      minDiffY = updateClosestGuide(
        guides,
        'horizontal',
        minDiffY,
        Math.abs(movingMetrics.centerY - objMetrics.centerY),
        objMetrics.centerY
      );

      minDiffY = updateClosestGuide(
        guides,
        'horizontal',
        minDiffY,
        Math.abs(movingMetrics.top - objMetrics.top),
        objMetrics.top
      );

      minDiffY = updateClosestGuide(
        guides,
        'horizontal',
        minDiffY,
        Math.abs(movingMetrics.bottom - objMetrics.bottom),
        objMetrics.bottom
      );
    });

    const canvasCenter = getCanvasCenter(canvas);

    minDiffX = updateClosestGuide(
      guides,
      'vertical',
      minDiffX,
      Math.abs(movingMetrics.centerX - canvasCenter.x),
      canvasCenter.x
    );

    minDiffY = updateClosestGuide(
      guides,
      'horizontal',
      minDiffY,
      Math.abs(movingMetrics.centerY - canvasCenter.y),
      canvasCenter.y
    );

    canvas.requestRenderAll();
  };

  const handleBeforeRender = () => {
    clearGuideLayer(canvas);
  };

  const handleAfterRender = () => {
    const ctx = getTopRenderContext(canvas);

    if (!ctx) {
      return;
    }

    const canvasSize = getCanvasSize(canvas);

    ctx.save();
    ctx.strokeStyle = CANVAS_GUIDELINE_COLOR;
    ctx.lineWidth = CANVAS_GUIDELINE_LINE_WIDTH;

    if (guides.vertical !== null) {
      const start = toViewportPoint(canvas, guides.vertical, 0);
      const end = toViewportPoint(canvas, guides.vertical, canvasSize.height);

      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
    }

    if (guides.horizontal !== null) {
      const start = toViewportPoint(canvas, 0, guides.horizontal);
      const end = toViewportPoint(canvas, canvasSize.width, guides.horizontal);

      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
    }

    ctx.restore();
  };

  const clearGuides = () => {
    resetGuides();
    canvas.requestRenderAll();
  };

  canvas.on('object:moving', handleMoving);
  canvas.on('before:render', handleBeforeRender);
  canvas.on('after:render', handleAfterRender);
  canvas.on('mouse:up', clearGuides);
  canvas.on('object:modified', clearGuides);
  canvas.on('selection:cleared', clearGuides);

  return () => {
    canvas.off('object:moving', handleMoving);
    canvas.off('before:render', handleBeforeRender);
    canvas.off('after:render', handleAfterRender);
    canvas.off('mouse:up', clearGuides);
    canvas.off('object:modified', clearGuides);
    canvas.off('selection:cleared', clearGuides);
  };
};

// TODO: Move following code to separate helper (CanvasEditor)
export const getCanvasResizeScale = (id: string, width: number, height: number) => {
  const scales = getCanvasDimensionScales(id, width, height);
  if (!scales) {
    return 1;
  }
  if (scales.widthScale < scales.heightScale) {
    return CANVAS_SCALE_MARGIN * Math.max(scales.widthScale, scales.heightScale);
  } else {
    return CANVAS_SCALE_MARGIN * Math.min(scales.widthScale, scales.heightScale);
  }
};

export const resizeCanvas = (
  canvas: fabric.Canvas,
  scale: number,
  width: number,
  height: number
) => {
  canvas.setWidth(width * scale);
  canvas.setHeight(height * scale);
  canvas?.zoomToPoint(new fabric.Point(0, 0), scale);
  canvas.calcOffset();
};
export const checkIsInvalidCanvasRef = (canvasRef: ForwardedRef<fabric.Canvas | null>) =>
  !canvasRef || typeof canvasRef !== 'object' || !('current' in canvasRef) || !canvasRef.current;

// TODO: Move following code to separate helper (CreateEditCoverPhoto)
export const getCanvasJson = (canvas: fabric.Canvas | null) => {
  return JSON.stringify(
    canvas?.toJSON([
      'width',
      'height',
      'viewportTransform',
      'data',
      'selectable',
      'editable',
      'borderColor',
      'transparentCorners',
      'borderDashArray',
      'selectionBackgroundColor',
      'cornerColor',
      'cornerDashArray',
      'cornerSize',
      'fill',
      'fontSize',
      'fontFamily',
      'textAlign',
      'lockMovementX',
      'lockMovementY',
      'lockScalingX',
      'lockScalingY',
      'lockRotation',
      'hasBorders',
      'hasControls',
      'hoverCursor',
      'opacity'
    ])
  );
};

export const setCanvasBackgroundImage = (
  canvas: fabric.Canvas,
  imgSrc: string,
  callback?: () => void
) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.src = imgSrc;
  image.onload = () => {
    const fabricImage = new fabric.Image(image);

    const scaleX = DEFAULT_CANVAS_SIZE.width / (fabricImage.width ?? 1);
    const scaleY = DEFAULT_CANVAS_SIZE.height / (fabricImage.height ?? 1);

    fabricImage.scaleX = scaleX;
    fabricImage.scaleY = scaleY;

    fabricImage.left = (DEFAULT_CANVAS_SIZE.width - (fabricImage.width ?? 1) * scaleX) / 2;
    fabricImage.top = (DEFAULT_CANVAS_SIZE.height - (fabricImage.height ?? 1) * scaleY) / 2;

    canvas.backgroundColor = undefined;
    canvas.setBackgroundImage(fabricImage, () => {
      canvas.backgroundImage = fabricImage;
      canvas.requestRenderAll();
      if (callback) {
        callback();
      }
    });
  };
};

export const removeCoverImage = (canvas: fabric.Canvas) => {
  const image = canvas
    .getObjects('image')
    ?.find((image) => image.data.id === 'profileImage') as fabric.Object;
  if (image) {
    canvas.remove(image);
  }
};

export const setCoverImage = (
  canvas: fabric.Canvas,
  imgSrc: string,
  moveObjects: boolean,
  addStroke = true
) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.src = imgSrc;

  image.onload = () => {
    const fabricImage = new fabric.Image(image);

    fabricImage.setControlsVisibility({
      mt: false,
      mb: false,
      ml: false,
      mr: false
    });

    const scale = getClipScale(fabricImage.width ?? 0, fabricImage.height ?? 0);

    // Set scale and position the image
    fabricImage.scaleX = scale;
    fabricImage.scaleY = scale;
    fabricImage.selectable = moveObjects;
    fabricImage.lockMovementX = !moveObjects;
    fabricImage.lockMovementY = !moveObjects;
    fabricImage.lockScalingX = !moveObjects;
    fabricImage.lockScalingY = !moveObjects;
    fabricImage.lockRotation = !moveObjects;
    fabricImage.hoverCursor = 'default';
    fabricImage.hasControls = moveObjects;
    fabricImage.hasBorders = moveObjects;
    // Calculate position
    fabricImage.top =
      (DEFAULT_CANVAS_SIZE.height ?? 1) / 2 - ((fabricImage.height ?? 1) / 2) * scale;
    fabricImage.left =
      ((DEFAULT_CANVAS_SIZE.width ?? 1) * 3) / 4 -
      ((fabricImage.width ?? 1) / 2) * scale +
      CANVAS_THEME_CONFIG.clipImage.left;

    // Store custom data
    fabricImage.data = { id: 'profileImage' };

    if (addStroke) {
      fabricImage.stroke = 'white';
      fabricImage.strokeWidth = 20 * (1 / fabricImage.scaleX);
    }

    // Remove any existing cover images before adding the new one
    removeCoverImage(canvas);

    const canvasObjects = canvas.getObjects();
    const overlay = getOverlayFromObjects(canvasObjects);

    if (overlay) {
      const overlayIndex = canvasObjects.indexOf(overlay);
      canvas.insertAt(fabricImage, overlayIndex + 1, false);
    } else {
      canvas.insertAt(fabricImage, 0, false);
    }

    canvas.renderAll();
  };

  image.onerror = () => {
    // eslint-disable-next-line no-console
    console.error('Failed to load image:', imgSrc);
  };
};

export const createImageFromCanvas = (canvas: fabric.Canvas, format: string) => {
  const canvasParentElement = document.getElementById('canvas-wrapper');
  if (!canvasParentElement) {
    return null;
  }
  const availableWidth = canvasParentElement.clientWidth;
  const availableHeight = canvasParentElement.clientHeight;

  const widthScale = availableWidth / DEFAULT_CANVAS_SIZE.width;
  const heightScale = availableHeight / DEFAULT_CANVAS_SIZE.height;
  const scale = Math.min(widthScale, heightScale);
  return canvas.toDataURL({
    width: Math.floor(canvas.width ?? 0),
    height: canvas.height,
    multiplier: 1 / scale,
    left: 0,
    top: 0,
    format: format
  });
};

export const getProfileImageFromCanvas = (canvas: fabric.Canvas) => {
  return canvas.getObjects('image')?.find((image) => image.data.id === 'profileImage');
};

export const setBackgroundImageOpacity = (
  canvas: fabric.Canvas,
  lighten: boolean,
  opacity: number
) => {
  if (!canvas) {
    throw new Error('Canvas is not initialized.');
  }

  if (opacity === null || opacity < 0 || opacity > 1) {
    throw new Error('Invalid opacity value.');
  }

  const canvasObjects = canvas.getObjects();
  let overlay = getOverlayFromObjects(canvasObjects);

  if (!overlay) {
    overlay = new fabric.Rect({
      left: 0,
      top: 0,
      width: canvas.getWidth() * (1 / canvas.getZoom()),
      height: canvas.getHeight() * (1 / canvas.getZoom()),
      fill: lighten ? 'white' : 'black',
      opacity: opacity,
      selectable: false,
      evented: false,
      data: { id: BACKGROUND_OVERLAY }
    });
    canvas.add(overlay);
    canvas.sendToBack(overlay);
  }

  overlay.set({
    left: 0,
    top: 0,
    width: canvas.getWidth() * (1 / canvas.getZoom()),
    height: canvas.getHeight() * (1 / canvas.getZoom()),
    fill: lighten ? 'white' : 'black',
    opacity: opacity
  });

  canvas.requestRenderAll();
};

export const calculateOpacity = (overlay: fabric.Rect) => {
  if (!overlay) {
    return 0;
  }
  const opacityValue = (overlay.fill === 'white' ? 1 : -1) * (overlay.opacity ?? 0) * 100;
  return opacityValue;
};
// Shared, can stay here
export const getCanvasFromRef = (canvasRef: ForwardedRef<fabric.Canvas | null>) => {
  if (canvasRef && typeof canvasRef === 'object' && 'current' in canvasRef && canvasRef.current) {
    return canvasRef.current;
  }
  return null;
};

// TODO: Move following code to separate helper (TextOverlay)
export const checkIsCanvasEmpty = (canvasRef: ForwardedRef<fabric.Canvas | null>) => {
  const canvas = getCanvasFromRef(canvasRef);
  return canvas?.getObjects().length === 0;
};

export const getFullHDImageFromCanvas = async (
  canvas: fabric.Canvas,
  fileName: string
): Promise<File> => {
  if (!canvas || !canvas.width || !canvas.height) {
    throw new Error('Canvas is not initialized');
  }
  const scaleX = FULL_HD_DIMENSION.WIDTH / canvas.width;
  const scaleY = FULL_HD_DIMENSION.HEIGHT / canvas.height;
  const multiplier = Math.min(scaleX, scaleY);
  const dataURL = canvas.toDataURL({
    format: 'png',
    quality: 1,
    multiplier
  });

  const response = await fetch(dataURL);
  const blob = await response.blob();

  return new File([blob], fileName, { type: 'image/png' });
};

export const areAllSelectedOfType = (
  selectedIds: string[],
  selectedObjects: Record<string, fabric.Object>,
  type: string
) => selectedIds.every((id) => selectedObjects[id].type !== type);

export const removeActiveObjects = (canvas: fabric.Canvas) => {
  const activeObjects = canvas.getActiveObjects();
  if (activeObjects.length > 0) {
    activeObjects.forEach((obj) => canvas.remove(obj));
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  }
};

export const getCanvasImageScale = (
  canvas: fabric.Canvas,
  imageWidth: number,
  imageHeight: number
) => {
  const canvasWidth = canvas.getWidth();
  const canvasHeight = canvas.getHeight();

  const imgWidth = imageWidth as number;
  const imgHeight = imageHeight as number;

  const needsScaling = imgWidth > canvasWidth || imgHeight > canvasHeight;
  let scaleFactor = 1;
  if (needsScaling) {
    scaleFactor = Math.min(canvasWidth / imgWidth, canvasHeight / imgHeight);
  }
  return scaleFactor;
};
